import { Input } from "@groot/ui/input";
import { Label } from "@groot/ui/label";
import { Textarea } from "@groot/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@groot/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@groot/ui/card";
import { Filter, Trash2, X } from "lucide-react";
import { Button } from "@groot/ui/button";
import { ModelSelect } from "@/app/shared/components/model-select";
import { TRIGGER_TYPES, ACTION_TYPES, type WorkflowFormData } from "./constants";
import { useAccounts } from "@/hooks/api/useAccounts";
import type { ActionType, TriggerType } from "@/types";

interface WorkflowPropertyPanelProps {
  nodeId: string; // 'trigger' or 'action-INDEX'
  formData: WorkflowFormData;
  setFormData: (data: WorkflowFormData) => void;
  onClose: () => void;
  onDeleteAction?: (index: number) => void;
}

export function WorkflowPropertyPanel({
  nodeId,
  formData,
  setFormData,
  onClose,
  onDeleteAction,
}: WorkflowPropertyPanelProps) {
  const { data: accounts } = useAccounts();
  const isTrigger = nodeId === "trigger";
  const actionIndex = !isTrigger ? Number.parseInt(nodeId.split("-")[1]) : -1;

  // Use optional chaining for safety, though the check below handles it.
  const action = !isTrigger && formData.actions[actionIndex] ? formData.actions[actionIndex] : null;

  if (!isTrigger && !action) return null;

  const updateAction = (field: string, value: string) => {
    const newActions = [...formData.actions];
    if (newActions[actionIndex]) {
      newActions[actionIndex] = { ...newActions[actionIndex], [field]: value };
      setFormData({ ...formData, actions: newActions });
    }
  };

  return (
    <div className="h-full flex flex-col bg-background border-l border-border w-[400px] overflow-y-auto shadow-xl">
      <div className="p-4 border-b border-border flex items-center justify-between bg-muted">
        <h3 className="font-medium text-foreground">
          {isTrigger ? "Trigger Configuration" : "Action Configuration"}
        </h3>
        <Button variant="ghost" size="icon" onClick={onClose}>
          <X className="size-4" />
        </Button>
      </div>

      <div className="p-4 space-y-6">
        {isTrigger ? (
          <div className="space-y-4">
            <div>
              <Label htmlFor="triggerType">Trigger Type</Label>
              <Select
                value={formData.triggerType}
                onValueChange={(value: TriggerType) =>
                  setFormData({ ...formData, triggerType: value })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TRIGGER_TYPES.map((trigger) => (
                    <SelectItem key={trigger.value} value={trigger.value}>
                      {trigger.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Trigger Filters */}
            <Card>
              <CardHeader className="p-4 pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Filter className="size-4" />
                  Conditions
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 pt-2 space-y-3">
                {formData.triggerType === "scheduled" && (
                  <div>
                    <Label htmlFor="cronExpression">Cron Expression</Label>
                    <Input
                      id="cronExpression"
                      value={formData.cronExpression}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          cronExpression: e.target.value,
                        })
                      }
                      placeholder="e.g., 0 0 * * *"
                    />
                    <p className="text-xs text-muted-foreground mt-1">Daily: 0 0 * * *</p>
                  </div>
                )}

                {formData.triggerType === "email_received" && (
                  <>
                    <div>
                      <Label htmlFor="emailFrom">From Email</Label>
                      <Input
                        id="emailFrom"
                        type="email"
                        value={formData.emailFrom}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            emailFrom: e.target.value,
                          })
                        }
                        placeholder="e.g., bank@example.com"
                      />
                    </div>
                    <div>
                      <Label htmlFor="emailSubjectContains">Subject Contains</Label>
                      <Input
                        id="emailSubjectContains"
                        value={formData.emailSubjectContains}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            emailSubjectContains: e.target.value,
                          })
                        }
                      />
                    </div>
                    <div>
                      <Label htmlFor="emailBodyContains">Body Contains</Label>
                      <Input
                        id="emailBodyContains"
                        value={formData.emailBodyContains}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            emailBodyContains: e.target.value,
                          })
                        }
                      />
                    </div>
                    <div>
                      <Label htmlFor="attachmentIgnoreList">Ignore Attachments</Label>
                      <Textarea
                        id="attachmentIgnoreList"
                        value={formData.attachmentIgnoreList}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            attachmentIgnoreList: e.target.value,
                          })
                        }
                        placeholder={"e.g.\n*terms*conditions*\nKFS"}
                        className="min-h-[80px] font-mono text-xs"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        One filename pattern per line. Emails often carry the statement plus extra
                        PDFs (T&amp;C, Key Fact Statement) — matched attachments are skipped so the
                        statement PDF is always picked. Use <code>*</code> as a wildcard or plain
                        text to match part of the name (case-insensitive).
                      </p>
                    </div>
                  </>
                )}

                {formData.triggerType === "manual" && (
                  <p className="text-sm text-muted-foreground">No conditions available</p>
                )}
              </CardContent>
            </Card>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label>Action Type</Label>
              {onDeleteAction && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-destructive hover:text-destructive hover:bg-destructive/10"
                  onClick={() => onDeleteAction(actionIndex)}
                >
                  <Trash2 className="size-4 mr-1" />
                  Delete
                </Button>
              )}
            </div>

            <Select
              value={action?.type}
              onValueChange={(value: ActionType) => updateAction("type", value)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ACTION_TYPES.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="space-y-4 border-t pt-4">
              {action?.type === "unlock_pdf" && (
                <div>
                  <Label>PDF Password</Label>
                  <Input
                    type="password"
                    value={action?.password || ""}
                    onChange={(e) => updateAction("password", e.target.value)}
                    placeholder="Enter PDF password"
                  />
                </div>
              )}

              {action?.type === "webhook" && (
                <div>
                  <Label>Webhook URL</Label>
                  <Input
                    type="url"
                    value={action?.url || ""}
                    onChange={(e) => updateAction("url", e.target.value)}
                    placeholder="https://example.com/webhook"
                  />
                </div>
              )}

              {action?.type === "add_as_statement" && accounts && (
                <>
                  <div>
                    <Label>Target Account</Label>
                    <Select
                      value={action?.accountId || ""}
                      onValueChange={(value) => {
                        const account = accounts?.find((a) => a.id.toString() === value);
                        const extractorConfig = account?.meta?.statementExtractor as
                          | Record<string, unknown>
                          | undefined;
                        const defaultPages = extractorConfig?.defaultPagesToIgnoreFromEnd;
                        const newActions = [...formData.actions];
                        if (newActions[actionIndex]) {
                          newActions[actionIndex] = {
                            ...newActions[actionIndex],
                            accountId: value,
                            ...(typeof defaultPages === "number"
                              ? { ignorePagesFromEnd: defaultPages }
                              : {}),
                          };
                          setFormData({ ...formData, actions: newActions });
                        }
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select account" />
                      </SelectTrigger>
                      <SelectContent>
                        {accounts.map((account) => (
                          <SelectItem key={account.id} value={account.id.toString()}>
                            {account.accountName}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Statement Type</Label>
                    <Select
                      value={action?.statementType || "extraction"}
                      onValueChange={(value) => updateAction("statementType", value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="extraction">Extraction</SelectItem>
                        <SelectItem value="archival">Archival</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="triggerExtraction"
                      checked={action?.triggerExtraction || false}
                      onChange={(e) => {
                        const newActions = [...formData.actions];
                        if (newActions[actionIndex]) {
                          newActions[actionIndex] = {
                            ...newActions[actionIndex],
                            triggerExtraction: e.target.checked,
                            ...(e.target.checked ? {} : { autoUpload: false }),
                          };
                          setFormData({ ...formData, actions: newActions });
                        }
                      }}
                      className="size-4"
                    />
                    <Label htmlFor="triggerExtraction">Trigger AI Extraction</Label>
                  </div>
                  {action?.triggerExtraction && (
                    <>
                      <div className="flex items-center space-x-2 ml-4">
                        <input
                          type="checkbox"
                          id="autoUpload"
                          checked={action?.autoUpload || false}
                          onChange={(e) => {
                            const newActions = [...formData.actions];
                            if (newActions[actionIndex]) {
                              newActions[actionIndex] = {
                                ...newActions[actionIndex],
                                autoUpload: e.target.checked,
                              };
                              setFormData({ ...formData, actions: newActions });
                            }
                          }}
                          className="size-4"
                        />
                        <Label htmlFor="autoUpload">Auto-upload extracted transactions</Label>
                      </div>
                      <div className="flex items-center space-x-2 ml-4">
                        <input
                          type="checkbox"
                          id="renameFile"
                          checked={action?.renameFile || false}
                          onChange={(e) => {
                            const newActions = [...formData.actions];
                            if (newActions[actionIndex]) {
                              newActions[actionIndex] = {
                                ...newActions[actionIndex],
                                renameFile: e.target.checked,
                              };
                              setFormData({ ...formData, actions: newActions });
                            }
                          }}
                          className="size-4"
                        />
                        <Label htmlFor="renameFile">Rename file to YYYY-MM.pdf</Label>
                      </div>
                      <div className="ml-4 mt-2">
                        <Label htmlFor="ignorePagesFromEnd">Ignore pages from end</Label>
                        <Input
                          id="ignorePagesFromEnd"
                          type="number"
                          min="0"
                          value={action?.ignorePagesFromEnd ?? ""}
                          onChange={(e) => {
                            const newActions = [...formData.actions];
                            if (newActions[actionIndex]) {
                              const value = e.target.value
                                ? Number.parseInt(e.target.value, 10)
                                : undefined;
                              newActions[actionIndex] = {
                                ...newActions[actionIndex],
                                ignorePagesFromEnd: value,
                              };
                              setFormData({ ...formData, actions: newActions });
                            }
                          }}
                          placeholder="0"
                          className="mt-1"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          Skip last X pages (e.g., T&amp;C, ads). Defaults to account config.
                        </p>
                      </div>
                      <div className="ml-4 mt-2">
                        <ModelSelect
                          value={action?.aiModel}
                          onChange={(modelId) => {
                            const newActions = [...formData.actions];
                            if (newActions[actionIndex]) {
                              newActions[actionIndex] = {
                                ...newActions[actionIndex],
                                aiModel: modelId,
                              };
                              setFormData({ ...formData, actions: newActions });
                            }
                          }}
                        />
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
